import Stepper from '@/components/Stepper';
import ToolBar from '@/components/ToolBar';
import { zodResolver } from '@hookform/resolvers/zod';
import { useContext, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { FormProvider, useForm } from 'react-hook-form';
import { ClientInfo } from '@pages/clients/steppers/ClientInfo';
import ClientDataExchange from './steppers/ClientDataExchange';
import ClientNotes from './steppers/ClientNotes';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import AlertBox from '@/components/AlertBox';
import { toast } from '@/components/ui/use-toast';
import {
  ClientContactSchema,
  ClientDataExchangeSchema,
  ClientInformationSchema,
  ClientInstructionSchema,
} from './client.schema';
import { joinByComma } from '@/lib/utils';
import type { ClientResponse, ClientUpdateRequest, ContactResponse } from '@/types/client.types';
import { useClient, useUpdateClient } from '@/services/clients';
import { getPhoneNumberValue } from '@/components/FormFieldPhone';
import Loader from '@/components/Loader';
import { NavigationBlockerContext } from '@/context/NavigationBlockerContext';
import { displayError } from '@/lib/error';
import { z } from 'zod';
import { NotFoundRedirectionWrapper } from '@/components/NotFoundRedirectWrapper';
import { ContactForm } from '@/components/ContactForm';
import { normalizeEmailList } from '@/utils/helper';

const schemas = [ClientInformationSchema, ClientContactSchema, ClientDataExchangeSchema, ClientInstructionSchema];

const filterEmails = (emails: string[], contactEmails: (string | undefined | null)[]) => {
  if (!contactEmails) return [];
  return emails?.filter((email) => contactEmails.includes(email));
};

export const ClientUpdate = ({
  displayType = 'PAGE',
  onSave,
  defaultActiveStep = 0,
  selectedClientId,
}: {
  displayType?: 'MODAL' | 'PAGE';
  onSave?: (client: ClientResponse) => void;
  defaultActiveStep?: number;
  selectedClientId?: string;
}) => {
  const [searchParam, setSearchParam] = useSearchParams();
  const [activeStep, setActiveStep] = useState(defaultActiveStep ?? 0);
  const [currentSchema, setCurrentSchema] = useState(schemas[activeStep]);
  const [previousPath, setPreviousPath] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const navigate = useNavigate();
  const clientId = useParams().clientId ?? selectedClientId;
  const form = useForm({
    resolver: zodResolver(currentSchema),
    mode: 'onBlur',
  });

  const { data: clientData, isFetching, isError } = useClient(clientId);
  const { mutate: updateClient, status } = useUpdateClient();
  const {
    trigger,
    getValues,
    reset,
    formState: { isDirty, isValid },
  } = form;

  const { setIsDirty } = useContext(NavigationBlockerContext);

  useEffect(() => {
    setIsDirty(isDirty);
  }, [isDirty, setIsDirty]);

  useEffect(() => {
    if (searchParam.get('contactId') ?? searchParam.get('contactMode'))
      setPreviousPath(`/clients/${clientId}/contacts`);
    else setPreviousPath('/clients');
  }, []);

  const saveHandler = () => {
    const values = getValues();
    const contactEmails = (values.contacts as z.infer<typeof ClientContactSchema>['contacts']).map(
      (contact) => contact.email
    );
    values.phone = getPhoneNumberValue(values.phone as string) ?? null;
    values.fax = getPhoneNumberValue(values.fax as string) ?? null;
    values.affidavit = filterEmails(values.affidavit as string[], contactEmails);
    values.arInvoices = filterEmails(values.arInvoices as string[], contactEmails);
    values.finalBillingSummary = filterEmails(values.finalBillingSummary as string[], contactEmails);
    values.recapAndInvoice = filterEmails(values.recapAndInvoice as string[], contactEmails);
    values.affidavitWithInvoice = values.canAffidavitWithInvoice
      ? filterEmails(values.affidavitWithInvoice as string[], contactEmails)
      : [];
    values.statement = filterEmails(values.statement as string[], contactEmails);
    values.affidavitWithInvoiceCc = normalizeEmailList(values.affidavitWithInvoiceCc as string);
    values.arInvoicesCc = normalizeEmailList(values.arInvoicesCc as string);
    values.finalBillingSummaryCc = normalizeEmailList(values.finalBillingSummaryCc as string);
    values.recapAndInvoiceCc = normalizeEmailList(values.recapAndInvoiceCc as string);
    values.affidavitCc = normalizeEmailList(values.affidavitCc as string);
    values.statementCc = normalizeEmailList(values.statementCc);
    values.contacts = (values.contacts as ContactResponse[]).map((contact) => {
      return {
        ...contact,
        phone: getPhoneNumberValue(contact.phone) ?? null,
        emailStatus: (contact.emailStatus = 'NONE'),
        cell: getPhoneNumberValue(contact.cell) ?? null,

        fax: getPhoneNumberValue(contact.fax) ?? null,
      };
    });
    delete values.createdAt;
    delete values.updatedAt;
    delete values.deactivatedAt;
    if (values.type === 'FIRM') {
      delete values.firstName;
      delete values.lastName;
    }
    if (values.type === 'INDIVIDUAL') {
      delete values.clientName;
    }
    updateClient(
      { data: values as ClientUpdateRequest, id: clientId! },
      {
        onSuccess: (data) => {
          displayType === 'PAGE' && navigate(previousPath);
          onSave && onSave(data);
          toast({
            variant: 'default',
            description: `${clientData?.clientName} has been updated.`,
            duration: 3000,
          });
        },
        onError: (e) => {
          displayError(e, 5000);
        },
      }
    );
  };

  useEffect(() => {
    if (clientData) {
      const client = clientData;
      reset({
        ...client,
        arInvoicesCc: joinByComma(client.arInvoicesCc!, ', '),
        finalBillingSummaryCc: joinByComma(client.finalBillingSummaryCc!, ', '),
        recapAndInvoiceCc: joinByComma(client.recapAndInvoiceCc!, ', '),
        affidavitWithInvoiceCc: joinByComma(client.affidavitWithInvoiceCc!, ', '),
        affidavitCc: joinByComma(client.affidavitCc!, ', '),
        statementCc: joinByComma(client.statementCc!, ', '),
      });
      setRefreshKey((prev) => prev + 1);
    }
  }, [clientData]);

  const [steps] = useState([
    { title: 'Client Information', isCompleted: true },
    { title: 'Employees/ Contact', isCompleted: true },
    { title: 'Data Exchange Contacts', isCompleted: true },
    { title: 'Notes / Instructions', isCompleted: true },
  ]);

  useEffect(() => {
    const activeStep = searchParam.get('stepper');
    if (activeStep && displayType === 'PAGE') {
      const index = steps.findIndex((step) => step.title === activeStep);
      setActiveStep(index);
    }
  }, [searchParam]);

  useEffect(() => {
    setCurrentSchema(schemas[activeStep]);
  }, [activeStep]);

  const goToStep = async (step: number) => {
    const isValid = await trigger();
    if (isValid) {
      if (steps[step].isCompleted || step <= activeStep) {
        displayType === 'PAGE' && setSearchParam({ stepper: steps[step].title });
        setActiveStep(step);
        if (displayType === 'MODAL' && searchParam.get('contactMode')) {
          const updatedParams = Object.fromEntries(
            Array.from(searchParam.entries()).filter(([key]) => key !== 'contactMode')
          );
          setSearchParam(updatedParams, { replace: true });
        }
      }
    }
  };
  return (
    <NotFoundRedirectionWrapper isItemNotFound={isError} itemName="client">
      <div className="w-full scrollbar-hide h-full">
        <Loader isLoading={isFetching}>
          {displayType === 'PAGE' && <ToolBar title={clientData?.clientName} />}

          <div className="mt-3 flex justify-center pl-24">
            <Stepper steps={steps} activeStep={activeStep} goToStep={goToStep} mode="edit" />
          </div>

          <FormProvider {...form}>
            {activeStep === 0 && <ClientInfo />}
            {activeStep === 1 && (
              <ContactForm key={refreshKey} mode="edit" isFetching={isFetching} contactFor="CLIENT" />
            )}
            {activeStep === 2 && <ClientDataExchange mode="edit" goToStep={goToStep} />}
            {activeStep === 3 && <ClientNotes />}
          </FormProvider>

          <div className={`flex justify-end mt-4 mb-8 w-full gap-2`}>
            {displayType === 'PAGE' && (
              <AlertBox
                triggerButton={
                  <Button
                    variant="outline"
                    className="text-primary py-2 px-4 rounded-md border-primary focus:outline-none flex justify-center w-[125px] h-[48px] hover:bg-[#EDF5FF] "
                  >
                    Cancel
                  </Button>
                }
                title="Are you sure you want to cancel?"
                description="All of your current changes will be lost."
                warning={true}
                cancelBtnLabel="No"
                confirmBtnLabel="Yes"
                confirmBtnClick={() => {
                  navigate(previousPath);
                }}
              />
            )}

            <AlertBox
              triggerButton={
                <Button
                  variant="outline"
                  className="text-[#fff] py-2 px-4 rounded-md bg-primary w-[125px] h-[48px] border-primary flex justify-center hover:bg-[#12375C] hover:text-white "
                  disabled={!isValid || !isDirty || status === 'pending'}
                >
                  Save
                </Button>
              }
              title="Do you want to update the record?"
              description="This action will overwrite the previous data with the new data entered."
              cancelBtnLabel="Cancel"
              confirmBtnLabel="Save"
              isLoading={status === 'pending'}
              confirmBtnClick={saveHandler}
            />
          </div>
        </Loader>
      </div>
    </NotFoundRedirectionWrapper>
  );
};
